<script setup lang="ts">
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import { api } from '@/api';
import { routenames } from '@/router/routenames';
import { computed, onMounted, ref } from 'vue';
import { LoadedEvent, useLoading } from '@/utils/useLoading';
import {
  instanceOfCommentTagType,
  JobAction,
  JobState,
  JobOccurrenceDto,
  WorkItemDto,
  WorkStepDto,
  ActionItemDto,
  JobOccurrenceDtoTypeEnum,
} from '@/api/generator';
import JobExecutionForm from './components/JobExecutionForm.vue';
import { FormMode } from '@/components/forms/types';
import JobExecutionProgress from './components/JobExecutionProgress.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import { useBackUrl } from '@/router/utils';
import PtwBackButton from '@/components/buttons/PtwBackButton.vue';
import Button from '@/components/buttons/Button.vue';
import { useTranslation } from 'i18next-vue';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import useCrudForComments from '@/components/comments/useCrudForComments';
import ConfirmedAction from '@/components/confirmationModals/ConfirmedAction.vue';
import Textarea from '@/components/forms/Textarea/Textarea.vue';
import { useRouter } from 'vue-router';
import { usePossibleActions } from '@/utils/actions';
import { useJobCompletionNotifications } from '@/utils/useJobCompletionNotifications';
import { useNotificationStore } from '@/stores/notificationStore';
import { I18nKey } from '@/utils/typings';
import { useEntityEventStream } from './utils/useEntityEventHub';
import { useUserConstants } from '@/utils/useUserConstants';
import { entityNotFoundErrorHandler } from '@/errorHandling';
import { ApiError } from '@/errorHandling/typeGuards';
import { useToken } from '@/utils/tokens/tokens';
import { useJobOccurrence } from './utils/useJobOccurrence';
import { until } from '@vueuse/core';
import { JobExecutionUrlAction } from './types';
import { get } from 'lodash';

const mode = ref<FormMode>('details');

const { t } = useTranslation();

interface JobExecutionViewProps {
  id: string;
  action?: JobExecutionUrlAction;
}

const { createEntityEventHookForJob } = useEntityEventStream();

const props = defineProps<JobExecutionViewProps>();

const { on: onEntityEvent, executeAndAwaitEvent } = createEntityEventHookForJob(props.id);

const router = useRouter();

const { getJobCompletionNotification } = useJobCompletionNotifications(t);
const notificationStore = useNotificationStore();
const { systemUser } = useUserConstants();

const backUrl = useBackUrl([routenames.jobOccurrences.overview, routenames.dashboard]);

async function goBack() {
  if (backUrl) router.push(backUrl);
}

const {
  action: loadJobOccurrence,
  loading: loadJobOccurrenceLoading,
  onLoaded,
} = useLoading(getJobOccurrence, true);

onEntityEvent(async (event) => {
  // Delete case is agreed to be ignored for now and could lead to errors
  if (event === 'Modified') {
    await loadJobOccurrence();
  }
});

const token = useToken({
  id: props.id,
  $type:
    JobOccurrenceDtoTypeEnum.PtwPtw100AppZeroApplicationSharedJobExecutionDtoJobOccurrenceDtoPtwPtw100AppZeroApplicationShared,
});

token.on.inUse(() => router.push({ name: routenames.jobOccurrences.overview }));

async function getJobOccurrence() {
  const { result } = await api.jobOccurrencesApi.getJobOccurrenceWithActiveState(
    {
      id: props.id,
      isActive: token.isValid.value,
    },
    [entityNotFoundErrorHandler(router)],
  );

  if (result) {
    resetForm({
      values: result,
    });
  }

  return result;
}

const {
  form: { resetForm, values },
} = useVeeForm<JobOccurrenceDto>({
  mode,
});

useJobOccurrence({ isActive: token.isValid });

const state = computed(() => values.state);

const statusEntity = computed(() => {
  const isApproved = values.reviewRequired && values.state === 'Approved';

  if (isApproved && values.approvalTime) {
    const approverUser = values.approverUser || systemUser;
    return {
      ...values,
      editingUser: approverUser,
      editingTime: values.approvalTime,
    };
  }

  // non-approved jobs will use original values
  return values;
});

function inAnyStateOf(JobStates: JobState[] | undefined) {
  return !!(state.value && JobStates?.includes(state.value));
}

const { enabled, visible, reason } = usePossibleActions(values);

const executionTooltip = computed(() => {
  const action = values.possibleActions?.find((a) => a.action === ActionItemDto.Execute);
  return action?.reason
    ? t(action.reason.key as I18nKey, action.reason.parameters || {})
    : undefined;
});

const actions = computed(() => {
  return {
    execute: {
      enabled:
        !isActionOrUpdateLoading.value &&
        enabled('Execute') &&
        inAnyStateOf([
          'New',
          'Paused',
          'ReworkRequested',
          'ReviewPending',
          'Completed',
          'Approved',
        ]),
      visible:
        visible('Execute') &&
        inAnyStateOf([
          'New',
          'Paused',
          'ReworkRequested',
          'ReviewPending',
          'Completed',
          'Approved',
        ]),
    },
    pause: {
      visible: inAnyStateOf(['InExecution']),
    },
    report: {
      visible: inAnyStateOf(['Completed', 'Approved']),
    },
    complete: {
      enabled:
        !isActionOrUpdateLoading.value &&
        inAnyStateOf(['InExecution']) &&
        enabled('Execute') &&
        token.isValid.value,
      visible: inAnyStateOf(['InExecution']) && visible('Execute'),
    },
    review: {
      enabled:
        !isActionOrUpdateLoading.value &&
        enabled('Review') &&
        inAnyStateOf(['ReviewPending', 'Approved']),
      visible: visible('Review') && inAnyStateOf(['ReviewPending', 'Approved']),
    },
    approve: {
      enabled:
        !isActionOrUpdateLoading.value &&
        enabled('Review') &&
        inAnyStateOf(['InReview']) &&
        token.isValid.value,
    },
    reject: {
      enabled:
        !isActionOrUpdateLoading.value &&
        enabled('Review') &&
        inAnyStateOf(['InReview']) &&
        token.isValid.value,
    },
  };
});

const executionBtnText = computed(() => {
  switch (state.value) {
    case 'Paused':
    case 'ReworkRequested':
      return t('JobExecutionContinue');
    case 'Completed':
    case 'ReviewPending':
      return t('JobExecutionReopen');
    case 'New':
    default:
      return t('JobExecutionStart');
  }
});

function checkAllWorkStepsPassed(workItems?: WorkItemDto[] | null) {
  return workItems
    ?.flatMap<WorkStepDto>((workitem: WorkItemDto) => workitem?.workSteps ?? [])
    .every(
      (workStep) => workStep.workState === 'Passed' || workStep.workState === 'WithoutEvaluation',
    );
}

const allWorkStepsPassed = computed(() => checkAllWorkStepsPassed(values.workItems));

const hasOpenWorkSteps = computed(() =>
  values.workItems
    ?.flatMap<WorkStepDto>((workitem: WorkItemDto) => workitem?.workSteps ?? [])
    .some((workStep) => workStep.workState === 'Open'),
);

const completeBtnText = computed(() => {
  if (values.reviewRequired) {
    if (values.autoApprove && allWorkStepsPassed.value) {
      return t('JobExecutionComplete');
    } else {
      return t('JobExecutionCompleteAndReview');
    }
  }
  return t('JobExecutionComplete');
});

const needsCompletionConfirmation = computed(
  () => hasOpenWorkSteps.value || (values.reviewRequired && !allWorkStepsPassed.value),
);

const needsApprovalConfirmation = computed(() => !allWorkStepsPassed.value);

const { createComment } = useCrudForComments(
  computed(() => values.id),
  'JobOccurrence',
);
const comment = ref<string | undefined>(undefined);

async function addComment(lifeCycleState: JobState) {
  if (comment.value) {
    const tags = instanceOfCommentTagType(lifeCycleState) ? [lifeCycleState] : undefined;
    await createComment(comment.value, [], tags);

    getJobOccurrence();
  }

  clearComment();
}

function clearComment() {
  comment.value = undefined;
}

async function updateJobExecution(action: JobAction, tokenRequired: boolean = true) {
  if (tokenRequired) {
    await token.request();
  }

  if (tokenRequired && !token.isValid.value) {
    throw new Error("token invalid, can't execute action");
  }

  return api.jobOccurrencesApi.updateJobExecution(
    {
      id: props.id,
      updateJobOccurrenceDto: {
        action,
      },
    },
    [
      (error) => {
        // if we got a response that a token is needed and we don't have a token this means
        // the token was removed during pending of the call
        // because we already check before the call to have a token
        // TODO: confirm which status `updateJobExecution` returns for a revoked token. It
        // declares 400/401/403/404 but not the 409 the token subsystem uses elsewhere, so
        // `notFound` is a guess.
        const tokenWasRevoked =
          tokenRequired && error.name === ApiError.notFound && !token.isValid.value;

        if (tokenWasRevoked) {
          return undefined;
        } else {
          return error;
        }
      },
    ],
  );
}

function updateActionInUrl(action?: JobExecutionUrlAction) {
  const currentRoute = router.currentRoute.value;

  router.replace({
    name: currentRoute.name,
    hash: currentRoute.hash,
    params: { ...currentRoute.params, action },
  });
}

const { action: applyAction, loading: isApplyActionLoading } = useLoading(
  async (
    action: JobAction,
    tokenRequired: boolean = true,
    resolve: 'awaitUpdate' | 'immediate' = 'awaitUpdate',
  ) => {
    const result = await executeAndAwaitEvent(updateJobExecution, 'Modified')(
      action,
      tokenRequired,
    );

    // wait for JobOccurrence to be updated
    if (result.success && resolve === 'awaitUpdate') {
      await new Promise<LoadedEvent>((resolve) => onLoaded(resolve));
    }

    const urlAction = get(
      <Record<JobAction, JobExecutionUrlAction>>{
        Execute: 'startExecution',
        Review: 'startReview',
      },
      action,
      undefined,
    );

    updateActionInUrl(urlAction);

    return result;
  },
);

const isActionOrUpdateLoading = computed(
  () => isApplyActionLoading.value || loadJobOccurrenceLoading.value,
);

async function approve() {
  if (actions.value.approve.enabled) {
    const { success } = await applyAction('Approve', true);

    if (success) {
      await addComment('Approved');

      await token.revoke();

      notificationStore.success(
        t('JobCompleted', {
          name: values.jobConfigurationName,
          iuid: values.jobConfigurationIuid,
        }),
      );

      await router.push({
        name: routenames.jobOccurrences.overview,
        hash: '#tab:completed',
      });
    }
  }
}

async function reject() {
  if (actions.value.reject.enabled) {
    const { success } = await applyAction('Reject', true, 'immediate');

    if (success) {
      await addComment('ReworkRequested');

      await token.revoke();

      await goBack();
    }
  }
}

async function startExecution() {
  if (actions.value.execute.enabled) {
    const { success } = await applyAction('Execute');

    if (success) {
      mode.value = 'execution';
    }
  }
}

async function pause() {
  const { success } = await applyAction('Pause', false, 'immediate');

  if (success) {
    await token.revoke();
    await goBack();
  }
}

async function review() {
  if (actions.value.review.enabled) {
    await applyAction('Review', true);
  }
}

async function complete() {
  if (actions.value.complete.enabled) {
    const { success, result } = await applyAction('Complete', false, 'immediate');

    if (success) {
      const commentTag = result?.state === 'ReviewPending' ? 'ReviewPending' : 'Completed';

      await addComment(commentTag);

      await token.revoke();

      const completionInfo = {
        isCompleted: true,
        requiresReview: values.reviewRequired ?? false,
        isAutoApproved: !!(values.reviewRequired && allWorkStepsPassed.value),
        isSentForReview: !!(values.reviewRequired && !allWorkStepsPassed.value),
      };

      const notification = getJobCompletionNotification(
        completionInfo,
        values.jobConfigurationName ?? undefined,
        values.jobConfigurationIuid ?? undefined,
      );

      if (notification) {
        notificationStore.success(notification.message || '');
      }

      await goBack();
    }
  }
}

const { action: executeInitialAction, loadingFirstTime: pageLoading } = useLoading(async () => {
  await loadJobOccurrence();

  if (actions.value.execute.enabled && props.action === 'startExecution') {
    await startExecution();
  }

  if (actions.value.review.enabled && props.action === 'startReview') {
    await review();
  }
});

onMounted(executeInitialAction);

// Handle edge cases when click on complete triggers blur event on input field
// that starts evaluation and update of the JobOccurrence
// the update procedure needs to be awaited to check if confirmation for action is required
async function waitForReload(startTimeout = 500, endTimeout = 5000) {
  if (await until(loadJobOccurrenceLoading).toBe(true, { timeout: startTimeout })) {
    await until(loadJobOccurrenceLoading).toBe(false, { timeout: endTimeout });
  }
}
</script>

<template>
  <BasePage :loading="pageLoading">
    <template #title>
      <BasePageTitle
        :subtitle="`${$t('JobOccurrence_one')}${values?.targetSystem?.name ? ' | ' + values.targetSystem.name : ''}`"
        :title="values.jobConfigurationName"
        :titleId="values.jobConfigurationIuid"
      />
    </template>

    <template #info>
      <JobExecutionProgress
        :value="values.workProgressSummary || undefined"
        class="d-flex flex-grow-1 justify-center"
      />
    </template>

    <template #status>
      <BasePageStatus :entity="statusEntity">
        <template #title> {{ $t('StateOfExecution') }}: </template>
      </BasePageStatus>
    </template>

    <template #actions>
      <!-- Review Button -->
      <Tooltip
        v-if="actions.review.visible"
        position="left"
        :disabled="actions.review.enabled || !reason('Review')"
      >
        <template #target="{ props: targetProps }">
          <div v-bind="targetProps">
            <Button
              :disabled="!actions.review.enabled"
              :label="$t('JobExecutionReview')"
              variant="primary"
              class="ml-2"
              @click="review()"
            />
          </div>
        </template>
        <template #tooltip>{{ reason('Review') }}</template>
      </Tooltip>

      <!-- Execute Button (Reopen uses secondary variant) -->
      <Tooltip
        v-if="actions.execute.visible"
        position="left"
        :disabled="actions.execute.enabled || !executionTooltip"
      >
        <template #target="{ props: targetProps }">
          <div v-bind="targetProps">
            <Button
              :disabled="!actions.execute.enabled"
              class="ml-2"
              :label="executionBtnText"
              :variant="executionBtnText === t('JobExecutionReopen') ? 'secondary' : 'primary'"
              @click="startExecution()"
            />
          </div>
        </template>
        <template #tooltip>{{ executionTooltip }}</template>
      </Tooltip>

      <!-- Complete Button -->
      <template v-if="actions.complete.visible">
        <ConfirmedAction
          :needsConfirmation="needsCompletionConfirmation"
          @confirmed="complete()"
          @cancel="clearComment()"
        >
          <template #default="{ confirmedAction }">
            <Tooltip position="left" :disabled="actions.complete.enabled || !executionTooltip">
              <template #target="{ props: targetProps }">
                <div v-bind="targetProps">
                  <Button
                    class="ml-2"
                    :label="completeBtnText"
                    :disabled="!actions.complete.enabled"
                    variant="primary"
                    @click="
                      async () => {
                        await waitForReload();
                        await confirmedAction();
                      }
                    "
                  />
                </div>
              </template>
              <template #tooltip>{{ executionTooltip }}</template>
            </Tooltip>
          </template>

          <template #dialog.title>
            {{
              values.reviewRequired && !(values.autoApprove && allWorkStepsPassed)
                ? $t('JobExecutionCompleteAndReviewConfirmationTitle')
                : $t('JobExecutionCompleteConfirmationTitle')
            }}
          </template>

          <template #dialog.text>
            <div class="pre-wrap">
              {{
                values.reviewRequired && !(values.autoApprove && allWorkStepsPassed)
                  ? $t('JobExecutionCompleteAndReviewConfirmationMsg')
                  : $t('JobExecutionCompleteConfirmationMsg')
              }}
            </div>

            <Textarea
              v-model="comment"
              :label="$t('Comment_one')"
              class="mt-4"
              :placeholder="$t('OptionalCommentPlaceholder')"
            />
          </template>

          <template #dialog.actions="{ cancel, confirm }">
            <Button :label="$t('Cancel')" variant="text" @click="cancel" />
            <Button
              :label="
                values.reviewRequired && !(values.autoApprove && allWorkStepsPassed)
                  ? $t('JobExecutionCompleteAndReview')
                  : $t('JobExecutionComplete')
              "
              variant="primary"
              @click="confirm"
            />
          </template>
        </ConfirmedAction>
      </template>

      <!-- Pause Button       -->
      <Tooltip position="left" :disabled="actions.complete.enabled || !executionTooltip">
        <template #target="{ props: targetProps }">
          <div v-bind="targetProps">
            <Button
              v-if="actions.pause.visible"
              :label="$t('JobExecutionPauseAndClose')"
              :disabled="!actions.complete.enabled"
              variant="secondary"
              class="ml-2"
              @click="pause()"
            />
          </div>
        </template>
        <template #tooltip>{{ executionTooltip }}</template>
      </Tooltip>

      <!-- Approve & Reject Buttons -->
      <template v-if="inAnyStateOf(['InReview'])">
        <ConfirmedAction @confirmed="reject()" @cancel="clearComment()">
          <template #default="{ confirmedAction }">
            <Tooltip position="left" :disabled="actions.reject.enabled || !reason('Review')">
              <template #target="{ props: targetProps }">
                <div v-bind="targetProps" class="d-inline">
                  <Button
                    class="ml-2"
                    :disabled="!actions.reject.enabled"
                    :label="$t('Reject')"
                    variant="primary"
                    @click="confirmedAction()"
                  />
                </div>
              </template>

              <template #tooltip>{{ reason('Review') }}</template>
            </Tooltip>
          </template>

          <template #dialog.title>
            {{ $t('JobExecutionRejectConfirmationTitle') }}
          </template>

          <template #dialog.text>
            <div class="pre-wrap">
              {{ $t('JobExecutionRejectConfirmationMsg') }}
            </div>

            <Textarea v-model="comment" :label="$t('Comment_one')" class="mt-4" />
          </template>

          <template #dialog.actions="{ cancel, confirm }">
            <Button :label="$t('Cancel')" variant="text" @click="cancel" />
            <Button
              :disabled="!comment"
              class="ml-2"
              :label="$t('Reject')"
              variant="primary"
              @click="confirm()"
            />
          </template>
        </ConfirmedAction>

        <ConfirmedAction
          :needsConfirmation="needsApprovalConfirmation"
          @confirmed="approve()"
          @cancel="clearComment()"
        >
          <template #default="{ confirmedAction }">
            <Tooltip position="left" :disabled="actions.approve.enabled || !reason('Review')">
              <template #target="{ props: targetProps }">
                <div v-bind="targetProps" class="d-inline">
                  <Button
                    color="success"
                    :disabled="!actions.approve.enabled"
                    class="ml-2"
                    :label="$t('Approve')"
                    variant="primary"
                    @click="confirmedAction()"
                  />
                </div>
              </template>

              <template #tooltip>{{ reason('Review') }}</template>
            </Tooltip>
          </template>

          <template #dialog.title>
            {{ $t('JobExecutionApproveConfirmationTitle') }}
          </template>

          <template #dialog.text>
            <div class="pre-wrap">
              {{ $t('JobExecutionapproveConfirmationMsg') }}
            </div>

            <Textarea
              v-model="comment"
              class="mt-4"
              :label="$t('Comment_one')"
              :placeholder="$t('OptionalCommentPlaceholder')"
            />
          </template>

          <template #dialog.actions="{ cancel, confirm }">
            <Button :label="$t('Cancel')" variant="text" @click="cancel" />
            <Button
              class="ml-2"
              color="success"
              :label="$t('Approve')"
              variant="primary"
              @click="confirm()"
            />
          </template>
        </ConfirmedAction>
      </template>

      <!-- Back Button -->
      <PtwBackButton
        v-if="mode !== 'execution' && backUrl"
        :disabled="isApplyActionLoading"
        :to="backUrl"
        class="ml-2"
      />
    </template>
    <JobExecutionForm v-if="values" :id="props.id" />
  </BasePage>
</template>
